import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { Lock, AlertCircle, ArrowRight } from 'lucide-react';

const PARTICLES = Array.from({ length: 12 }, (_, i) => ({
  delay: i * 1.2,
  size: 2 + (i % 3) * 1.5,
  left: `${(i * 8.3 + 4) % 100}%`,
  duration: 10 + (i % 4) * 2,
}));

const FloatingParticle: React.FC<{ delay: number; size: number; left: string; duration: number }> = ({
  delay, size, left, duration
}) => (
  <div
    className="auth-particle"
    style={{
      left,
      width: size,
      height: size,
      animationDelay: `${delay}s`,
      animationDuration: `${duration}s`,
    }}
  />
);

export const UpdatePassword: React.FC = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);

  const hash = window.location.hash;
  const params = new URLSearchParams(hash.replace('#', '?'));
  const accessToken = params.get('access_token');
  const type = params.get('type');

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    const initializeSession = async () => {
      if (!accessToken) {
        console.error('Invalid reset password URL - no access token');
        window.location.href = '/forgot-password';
        return;
      }

      try {
        const { data: { user }, error: verifyError } = await supabase.auth.verifyOtp({
          token_hash: accessToken,
          type: 'recovery'
        });

        if (verifyError) {
          console.error('Error verifying recovery token:', verifyError);
          window.location.href = '/forgot-password';
          return;
        }

        if (!user) {
          console.error('No user found for recovery token');
          window.location.href = '/forgot-password';
          return;
        }
      } catch (err) {
        console.error('Error during token verification:', err);
        window.location.href = '/forgot-password';
      }
    };

    initializeSession();
  }, [accessToken, type]);

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setError('Passwords do not match. Please try again.');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { error } = await supabase.auth.updateUser({
        password: password
      });

      if (error) throw error;

      window.location.href = '/login';
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to update password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex relative">
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=1920&q=80"
          alt="Singer performing on stage"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-purple-950/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-purple-950/50 via-transparent to-purple-950/30" />
      </div>

      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        {PARTICLES.map((p, i) => (
          <FloatingParticle key={i} {...p} />
        ))}

        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <Link to="/" className="flex items-center gap-3 group">
            <img
              src="/TSL_Logo.png"
              alt="The Singing League"
              className="h-24 w-24 object-contain"
            />
            <span className="text-white font-bold text-2xl tracking-tight">The Singing League</span>
          </Link>

          <div className={`max-w-md transition-all duration-1000 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ transitionDelay: '300ms' }}>
            <h2 className="text-4xl xl:text-5xl font-bold text-white leading-tight mb-6">
              Almost there,
              <span className="block mt-1 landing-text-gradient">set your new password.</span>
            </h2>
            <p className="text-white/50 text-lg leading-relaxed">
              Choose a strong password to keep your account secure and get back to performing.
            </p>
          </div>

          <div className={`flex gap-8 transition-all duration-1000 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ transitionDelay: '600ms' }}>
            {[
              { label: 'Encrypted', value: '256-bit' },
              { label: 'Secure Reset', value: '100%' },
              { label: 'Support Available', value: '24/7' },
            ].map((stat) => (
              <div key={stat.label}>
                <p className="text-white font-bold text-xl">{stat.value}</p>
                <p className="text-white/30 text-xs mt-0.5">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center relative overflow-hidden">
        <div className="absolute inset-0 backdrop-blur-xl bg-purple-950/20" />
        <div className="absolute inset-0 auth-grid-pattern opacity-[0.02]" />
        <div className="absolute top-0 right-0 w-96 h-96 bg-purple-500/8 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-violet-500/8 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />

        <div className="relative z-10 w-full max-w-md px-8 sm:px-12">
          <div className={`lg:hidden flex items-center gap-3 mb-12 transition-all duration-700 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <Link to="/" className="flex items-center gap-3">
              <img
                src="/TSL_Logo.png"
                alt="The Singing League"
                className="h-24 w-24 object-contain"
              />
              <span className="text-white font-bold text-2xl tracking-tight">The Singing League</span>
            </Link>
          </div>

          <div className={`mb-10 transition-all duration-700 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
            style={{ transitionDelay: '100ms' }}>
            <h1 className="text-4xl font-bold text-white mb-2 tracking-tight">New Password</h1>
            <p className="text-white/40">Enter your new password below</p>
          </div>

          <div className={`transition-all duration-700 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
            style={{ transitionDelay: '200ms' }}>
            {error && (
              <div className="bg-red-500/8 border border-red-500/15 rounded-xl p-4 mb-6 flex items-start backdrop-blur-sm auth-shake">
                <AlertCircle className="text-red-400 mr-3 mt-0.5 flex-shrink-0" size={16} />
                <p className="text-red-300 text-sm">{error}</p>
              </div>
            )}

            <form onSubmit={handleUpdatePassword} className="space-y-5">
              <div>
                <label className="block text-xs font-medium text-white/50 uppercase tracking-wider mb-2">New Password</label>
                <div className={`relative group rounded-xl transition-all duration-300 ${
                  focusedField === 'password' ? 'auth-input-glow' : ''
                }`}>
                  <Lock className={`absolute left-4 top-1/2 transform -translate-y-1/2 transition-colors duration-200 ${
                    focusedField === 'password' ? 'text-violet-400' : 'text-white/25'
                  }`} size={16} />
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onFocus={() => setFocusedField('password')}
                    onBlur={() => setFocusedField(null)}
                    className="w-full pl-11 pr-4 py-3.5 bg-white/[0.05] border border-white/[0.08] text-white rounded-xl focus:outline-none focus:border-violet-400/40 focus:bg-white/[0.07] transition-all duration-300 placeholder:text-white/20 text-sm"
                    placeholder="Enter new password"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-white/50 uppercase tracking-wider mb-2">Confirm Password</label>
                <div className={`relative group rounded-xl transition-all duration-300 ${
                  focusedField === 'confirm' ? 'auth-input-glow' : ''
                }`}>
                  <Lock className={`absolute left-4 top-1/2 transform -translate-y-1/2 transition-colors duration-200 ${
                    focusedField === 'confirm' ? 'text-violet-400' : 'text-white/25'
                  }`} size={16} />
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    onFocus={() => setFocusedField('confirm')}
                    onBlur={() => setFocusedField(null)}
                    className="w-full pl-11 pr-4 py-3.5 bg-white/[0.05] border border-white/[0.08] text-white rounded-xl focus:outline-none focus:border-violet-400/40 focus:bg-white/[0.07] transition-all duration-300 placeholder:text-white/20 text-sm"
                    placeholder="Confirm new password"
                    required
                  />
                </div>
              </div>

              <button
                type="submit"
                className="auth-btn-primary w-full group"
                disabled={loading}
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="auth-spinner mr-2" />
                    <span>Updating password...</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-center">
                    <span>Update Password</span>
                    <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform duration-200" />
                  </div>
                )}
              </button>
            </form>
          </div>

          <div className={`mt-8 transition-all duration-700 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
            style={{ transitionDelay: '400ms' }}>
            <div className="relative flex items-center my-6">
              <div className="flex-1 h-px bg-white/[0.06]" />
              <span className="px-4 text-xs text-white/20 uppercase tracking-wider">Remember it?</span>
              <div className="flex-1 h-px bg-white/[0.06]" />
            </div>

            <Link
              to="/login"
              className="block w-full py-3.5 rounded-xl border border-white/[0.08] text-white/60 text-sm font-medium text-center hover:bg-white/[0.04] hover:border-white/[0.15] hover:text-white/80 transition-all duration-300"
            >
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};
